import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { TwoFactorAuthSetup } from './security/TwoFactorAuthSetup';
import { supabase } from '../../utils/supabase';
import { useAuthStore } from '../../stores/authStore';

export function SecuritySettings() {
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [isTwoFactorEnabled, setIsTwoFactorEnabled] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [doingChangePassword, setDoingChangePassword] = useState(false);
  
  const user = useAuthStore(state => state.user);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset states
    setError(null);
    setSuccess(null);
    setIsLoading(true);
    
    try {
      // Validate passwords match
      if (newPassword !== confirmPassword) {
        setError('新しいパスワードが一致しません');
        return;
      }
      
      // Validate password strength
      if (newPassword.length < 8) {
        setError('パスワードは8文字以上である必要があります');
        return;
      }
      
      if (!/(?=.*[A-Za-z])(?=.*\d)/.test(newPassword)) {
        setError('パスワードは少なくとも1つの英字と1つの数字を含める必要があります');
        return;
      }
      
      if (!user?.email) {
        setError('ユーザー情報が取得できません');
        return;
      }
      
      // First verify the current password by signing in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword
      });
      
      if (signInError) {
        setError('現在のパスワードが正しくありません');
        return;
      }
      
      // Update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (updateError) {
        setError(`パスワードの更新に失敗しました: ${updateError.message}`);
        return;
      }
      
      // Success
      setSuccess('パスワードが正常に更新されました');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
    } catch (err) {
      setError('予期せぬエラーが発生しました。もう一度お試しください。');
      console.error('Password update error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-900">パスワードの変更</h3>
          {!doingChangePassword && (
            <button
              onClick={() => setDoingChangePassword(true)}
              className="inline-flex w-[150px] items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              パスワードを変更
            </button>
          )}
        </div>

        {doingChangePassword && (
          <form className="space-y-4" onSubmit={handleSubmit}>
        <div>
          <label className="block text-sm text-gray-700">現在のパスワード</label>
          <input
            type="password"
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            className="mt-1 block w-full py-2 px-2 rounded-md border border-blue-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm text-gray-700">新しいパスワード</label>
          <input
            type="password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            className="mt-1 block w-full py-2 px-2 rounded-md border border-blue-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
            minLength={8}
          />
        </div>
        <div>
          <label className="block text-sm text-gray-700">新しいパスワード（確認）</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            className="mt-1 block w-full py-2 px-2 rounded-md border border-blue-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-md">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}
        {success && (
          <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-3 rounded-md">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            <p>{success}</p>
          </div>
        )}
        <div className="flex gap-4 justify-end">
          <button
            type="button"
            onClick={() => {
          setDoingChangePassword(false);
          setError(null);
          setSuccess(null);
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
            }}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            キャンセル
          </button>
          <button
            type="submit"
            disabled={isLoading || !currentPassword || !newPassword || !confirmPassword}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            変更中...
          </>
            ) : (
          'パスワードを変更'
            )}
          </button>
        </div>
          </form>
        )}
      </div>

      <div className="pt-6 border-t">
        <h3 className="text-sm font-medium text-gray-900 mb-4">二段階認証</h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">
              {isTwoFactorEnabled
                ? '二段階認証が有効です'
                : 'アカウントの安全性を高めるため、二段階認証の設定を推奨します'}
            </p>
            {isTwoFactorEnabled && (
              <p className="text-xs text-gray-500 mt-1">
                最終更新: 2024年3月15日
              </p>
            )}
          </div>
            <button
            onClick={() => setShowTwoFactorSetup(true)}
            className={`inline-flex justify-center items-center w-[150px] px-4 py-2 border text-sm font-medium rounded-md transition-colors ${
              isTwoFactorEnabled
              ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
              : 'border-transparent text-white bg-blue-600 hover:bg-blue-700'
            }`}
            >
            {isTwoFactorEnabled ? '設定を変更' : '設定する'}
          </button>
        </div>
      </div>

      {showTwoFactorSetup && (
        <TwoFactorAuthSetup
          onClose={() => {
            setShowTwoFactorSetup(false);
            setIsTwoFactorEnabled(true);
          }}
        />
      )}
    </div>
  );
}